import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {
    CreateVersionRequest,
    RollbackVersionRequest,
    VersionComparison,
    VersionDetail,
    VersionHistoryResponse
} from "../../model/dto/api/selection-criteria-versioning.interface";
import {SelectionCriteriaVersionDto} from "../../model/dto/entity/selection-criteria-version.dto";

/**
 * Service for selection criteria versioning operations.
 * Implements F-016: Selection Criteria Versioning feature.
 */
@Injectable({
    providedIn: 'root'
})
export class SelectionCriteriaVersionService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/selectionCriteriaVersions')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-code-branch';
    }

    /**
     * Gets version history for a selection criteria.
     * Returns all versions with change summaries.
     */
    getVersionHistory(selectionCriteriaId: number): Observable<VersionHistoryResponse> {
        return this.http.get<VersionHistoryResponse>(
            `${this.apiUrl}/criteria/${selectionCriteriaId}/history`
        );
    }

    /**
     * Creates a new version of the selection criteria.
     * Captures the current state as a new version with timestamp.
     */
    createVersion(selectionCriteriaId: number, request: CreateVersionRequest): Observable<SelectionCriteriaVersionDto> {
        return this.http.post<SelectionCriteriaVersionDto>(
            `${this.apiUrl}/criteria/${selectionCriteriaId}/versions`,
            request
        );
    }

    /**
     * Gets detailed information about a specific version.
     * Includes the criteria snapshot and usage history.
     */
    getVersionDetail(versionId: number): Observable<VersionDetail> {
        return this.http.get<VersionDetail>(`${this.apiUrl}/${versionId}/detail`);
    }

    /**
     * Rolls back to a previous version.
     * The specified version becomes the active criteria.
     */
    rollbackToVersion(selectionCriteriaId: number, request: RollbackVersionRequest): Observable<SelectionCriteriaVersionDto> {
        return this.http.post<SelectionCriteriaVersionDto>(
            `${this.apiUrl}/criteria/${selectionCriteriaId}/rollback`,
            request
        );
    }

    /**
     * Activates a specific version.
     */
    activateVersion(versionId: number): Observable<SelectionCriteriaVersionDto> {
        return this.http.post<SelectionCriteriaVersionDto>(
            `${this.apiUrl}/${versionId}/activate`,
            {}
        );
    }

    /**
     * Compares two versions of selection criteria.
     */
    compareVersions(selectionCriteriaId: number, fromVersion: number, toVersion: number): Observable<VersionComparison> {
        return this.http.get<VersionComparison>(
            `${this.apiUrl}/criteria/${selectionCriteriaId}/compare`,
            {params: {fromVersion: fromVersion.toString(), toVersion: toVersion.toString()}}
        );
    }
}
